/**
 * LandingPage — Public entry point for ARIA Training Portal.
 *
 * Shown at "/" (basename /app) before authentication.
 * Contains a login form for Training Portal (agent / manager roles).
 *
 * Successful login → POST /api/v1/auth/login, store token in sessionStorage, navigate /home
 * /home displays the DashboardPage (landing dashboard with learning progress, upcoming deadlines, etc.)
 *
 * Admin-only users (role=admin) are rejected with FORBIDDEN error — they should use /admin portal instead.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LandingPage.module.css';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

// In AWS both SPAs are served behind the same domain, path-routed: the admin
// portal lives at `/admin/` on the same origin. Only fall back to the local
// dev port (5174) when running against localhost.
const ADMIN_PORTAL_URL: string =
  (import.meta.env.VITE_ADMIN_PORTAL_URL as string | undefined) ??
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? `${window.location.protocol}//${window.location.hostname}:5174/admin/`
    : `${window.location.origin}/admin/`);

// Portal access is decided by the backend (`user.portal`, derived from role via
// ADR-011 B-7) — never re-derive it from the role string here, which drifts.
const TRAINING_ALLOWED_PORTALS = new Set(['training']);
const ADMIN_ALLOWED_PORTALS = new Set(['admin', 'platform']);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Portal = 'training' | 'admin';

type LoginError =
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_LOCKED'
  | 'FORBIDDEN_TRAINING'
  | 'FORBIDDEN_ADMIN'
  | 'NETWORK_ERROR'
  | null;

interface LoginResponseData {
  access_token: string;
  refresh_token: string;
  user: { role: string; portal: string; language: string; [key: string]: unknown };
}

// ---------------------------------------------------------------------------
// Auth API (no shared client — no Bearer token yet)
// ---------------------------------------------------------------------------

async function postLogin(
  email: string,
  password: string,
): Promise<{ status: string; data?: LoginResponseData; error?: { code: string } }> {
  const res = await fetch('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return res.json() as Promise<{
    status: string;
    data?: LoginResponseData;
    error?: { code: string };
  }>;
}

// ---------------------------------------------------------------------------
// Small SVG icons
// ---------------------------------------------------------------------------

function IconUsers({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconSettings({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function IconBarChart({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}
function IconBook({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
function IconShield({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function IconAward({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
}
function IconBuilding({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="9" height="15" /><rect x="13" y="2" width="9" height="20" /><path d="M6 7V3" />
    </svg>
  );
}
function IconUser({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function IconGlobe({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
function IconChevronDown({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function IconClose({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function IconStar({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function IconEye({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconEyeOff({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// App mockup (right panel — hero state)
// ---------------------------------------------------------------------------

function AppMockup() {
  return (
    <div className={styles.mockupPhone}>
      <div className={styles.mockupPhoneBar}>
        <span className={styles.mockupPhoneBarText}>Your Learning Journey</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </div>
      <div className={styles.mockupPhoneBody}>
        <p className={styles.mockupGreeting}>Good Morning, John! 👋</p>
        <div className={styles.mockupProgressRing}>
          <svg width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#dde1ed" strokeWidth="4" />
            <circle cx="18" cy="18" r="14" fill="none" stroke="#0055a5" strokeWidth="4"
              strokeDasharray="65.97 101.5" strokeDashoffset="25.5" strokeLinecap="round" />
            <text x="18" y="22" textAnchor="middle" fontSize="8" fontWeight="700" fill="#0055a5">75%</text>
          </svg>
          <div>
            <div className={styles.mockupProgressText}>75%</div>
            <div className={styles.mockupProgressSub}>Keep going! You're doing great.</div>
          </div>
        </div>
        <div className={styles.mockupCard}>
          <div className={styles.mockupCardTitle}>Claims Fundamentals</div>
          <div className={styles.mockupCardSub}>Module 3 of 6</div>
          <div className={styles.mockupBar}><div className={styles.mockupBarFill} style={{ width: '66%' }} /></div>
          <div className={styles.mockupContinueBtn}>Continue →</div>
        </div>
        <div>
          <div className={styles.mockupProgressLabel}>Upcoming Due</div>
          <div className={styles.mockupDueItem}><div className={styles.mockupDueDot} /><span>Fraud Detection — Due in 5 days</span></div>
          <div className={styles.mockupDueItem}><div className={styles.mockupDueDot} style={{ background: '#d9534f' }} /><span>AML Compliance — Due in 10 days</span></div>
        </div>
      </div>
    </div>
  );
}

function AgentIllustration() {
  return (
    <svg className={styles.agentFigure} viewBox="0 0 160 280" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="42" y="100" width="76" height="110" rx="10" fill="#1a3a6b" />
      <rect x="70" y="100" width="20" height="110" fill="#0055a5" />
      <rect x="76" y="110" width="8" height="60" rx="4" fill="#ffffff" opacity="0.3" />
      <circle cx="80" cy="75" r="28" fill="#f5c6a0" />
      <ellipse cx="80" cy="52" rx="26" ry="12" fill="#3d2c1e" />
      <circle cx="70" cy="75" r="3" fill="#3d2c1e" />
      <circle cx="90" cy="75" r="3" fill="#3d2c1e" />
      <path d="M72 86 Q80 93 88 86" stroke="#3d2c1e" strokeWidth="2" fill="none" strokeLinecap="round" />
      <rect x="12" y="108" width="32" height="16" rx="8" fill="#1a3a6b" />
      <rect x="116" y="108" width="32" height="16" rx="8" fill="#1a3a6b" />
      <rect x="8" y="118" width="52" height="70" rx="6" fill="#ffffff" stroke="#dde1ed" strokeWidth="1" />
      <rect x="12" y="122" width="44" height="52" rx="4" fill="#e8f0fb" />
      <rect x="14" y="124" width="40" height="6" rx="2" fill="#0055a5" />
      <rect x="14" y="134" width="30" height="3" rx="1.5" fill="#dde1ed" />
      <rect x="14" y="140" width="36" height="3" rx="1.5" fill="#dde1ed" />
      <rect x="14" y="146" width="24" height="3" rx="1.5" fill="#dde1ed" />
      <rect x="14" y="155" width="40" height="8" rx="4" fill="#0055a5" opacity="0.8" />
      <rect x="52" y="210" width="24" height="60" rx="8" fill="#0d1b3e" />
      <rect x="84" y="210" width="24" height="60" rx="8" fill="#0d1b3e" />
      <ellipse cx="64" cy="268" rx="18" ry="7" fill="#1a1a2e" />
      <ellipse cx="96" cy="268" rx="18" ry="7" fill="#1a1a2e" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Inline Login Panel
// ---------------------------------------------------------------------------

interface LoginPanelProps {
  initialPortal: Portal;
  onClose: () => void;
}

function LoginPanel({ initialPortal, onClose }: LoginPanelProps) {
  const navigate = useNavigate();
  const [portal, setPortal] = useState<Portal>(initialPortal);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<LoginError>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Focus email on mount / portal switch
  useEffect(() => {
    emailRef.current?.focus();
  }, [portal]);

  // Reset form state when switching portals
  function switchPortal(p: Portal) {
    setPortal(p);
    setLoginError(null);
    setEmail('');
    setPassword('');
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (isSubmitting) return;
      setLoginError(null);
      setIsSubmitting(true);

      try {
        const body = await postLogin(email, password);

        if (body.status === 'error') {
          const code = body.error?.code ?? '';
          setLoginError(code === 'ACCOUNT_LOCKED' ? 'ACCOUNT_LOCKED' : 'INVALID_CREDENTIALS');
          return;
        }

        const data = body.data;
        if (!data) { setLoginError('INVALID_CREDENTIALS'); return; }

        if (portal === 'training') {
          if (!TRAINING_ALLOWED_PORTALS.has(data.user.portal)) {
            setLoginError('FORBIDDEN_TRAINING');
            return;
          }
          sessionStorage.setItem('access_token', data.access_token);
          sessionStorage.setItem('refresh_token', data.refresh_token);
          sessionStorage.setItem('user_role', data.user.role);
          sessionStorage.setItem('user_language', data.user.language);
          sessionStorage.setItem('user_profile', JSON.stringify(data.user));
          navigate('/home', { replace: true });

        } else {
          // Admin portal — backend must have routed this user to admin/platform
          if (!ADMIN_ALLOWED_PORTALS.has(data.user.portal)) {
            setLoginError('FORBIDDEN_ADMIN');
            return;
          }
          // sessionStorage is origin-scoped and won't transfer cross-port.
          // Pass tokens in the URL hash fragment (never sent to server) so the
          // admin portal can absorb them into its own sessionStorage on load.
          const params = new URLSearchParams({
            t: data.access_token,
            r: data.refresh_token,
            role: data.user.role,
            lang: data.user.language,
            profile: JSON.stringify(data.user),
          });
          window.location.href = `${ADMIN_PORTAL_URL}#${params.toString()}`;
        }
      } catch {
        setLoginError('NETWORK_ERROR');
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, isSubmitting, portal, navigate],
  );

  function errorMessage(): string | null {
    switch (loginError) {
      case 'INVALID_CREDENTIALS': return 'Invalid email or password. Please try again.';
      case 'ACCOUNT_LOCKED': return 'Your account has been locked. Please contact support.';
      case 'FORBIDDEN_TRAINING': return 'This account does not have Training Portal access. Use Admin Portal instead.';
      case 'FORBIDDEN_ADMIN': return 'This account does not have Admin Portal access. Use Training Portal instead.';
      case 'NETWORK_ERROR': return 'Unable to reach the server. Please check your connection.';
      default: return null;
    }
  }

  const errMsg = errorMessage();
  const canSubmit = email.trim() !== '' && password !== '' && !isSubmitting;

  return (
    <div className={styles.loginPanel}>
      {/* Header */}
      <div className={styles.loginPanelHeader}>
        <div className={styles.loginPanelLogoRow}>
          <div className={styles.loginPanelLogoMark}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1.5" fill="white" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.5" />
            </svg>
          </div>
          <span className={styles.loginPanelBrand}>ARIA</span>
        </div>
        <button className={styles.loginPanelClose} onClick={onClose} aria-label="Close login panel">
          <IconClose color="#7a7a9a" />
        </button>
      </div>

      <h2 className={styles.loginPanelTitle}>Sign In to ARIA</h2>
      <p className={styles.loginPanelSub}>Choose your portal and enter your credentials.</p>

      {/* Portal selector tabs */}
      <div className={styles.portalTabs}>
        <button
          className={`${styles.portalTab} ${portal === 'training' ? styles.portalTabActive : ''}`}
          onClick={() => switchPortal('training')}
          type="button"
        >
          <div className={styles.portalTabIcon} style={{ background: portal === 'training' ? '#e8f0fb' : '#f5f7fc' }}>
            <IconUser color={portal === 'training' ? '#0055a5' : '#7a7a9a'} />
          </div>
          <div className={styles.portalTabText}>
            <span className={styles.portalTabName}>Training Portal</span>
            <span className={styles.portalTabDesc}>Agents &amp; Managers</span>
          </div>
        </button>

        <button
          className={`${styles.portalTab} ${portal === 'admin' ? styles.portalTabActive : ''}`}
          onClick={() => switchPortal('admin')}
          type="button"
        >
          <div className={styles.portalTabIcon} style={{ background: portal === 'admin' ? '#e6f4ed' : '#f5f7fc' }}>
            <IconSettings color={portal === 'admin' ? '#1a7a4a' : '#7a7a9a'} />
          </div>
          <div className={styles.portalTabText}>
            <span className={styles.portalTabName}>Admin Portal</span>
            <span className={styles.portalTabDesc}>Managers &amp; Administrators</span>
          </div>
        </button>
      </div>

      {/* Portal context hint */}
      <div className={styles.portalHint} style={{
        background: portal === 'training' ? '#e8f0fb' : '#e6f4ed',
        borderColor: portal === 'training' ? '#b3d0f5' : '#a8dbbe',
      }}>
        <span style={{ color: portal === 'training' ? '#0055a5' : '#1a7a4a', fontSize: '0.78rem', fontWeight: 500 }}>
          {portal === 'training'
            ? '🎓 Training Portal — access courses, roleplay sessions, and certifications.'
            : '⚙️ Admin Portal — manage teams, users, courses, and view compliance reports.'}
        </span>
      </div>

      {/* Error banner */}
      {errMsg !== null && (
        <div className={styles.loginPanelError} role="alert">{errMsg}</div>
      )}

      {/* Login form */}
      <form className={styles.loginForm} onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className={styles.loginField}>
          <label htmlFor="lp-email" className={styles.loginLabel}>Email address</label>
          <input
            ref={emailRef}
            id="lp-email"
            type="email"
            className={styles.loginInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@allianz.com.tw"
            required
            autoComplete="email"
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.loginField}>
          <label htmlFor="lp-password" className={styles.loginLabel}>Password</label>
          <div className={styles.loginPasswordWrapper}>
            <input
              id="lp-password"
              type={showPassword ? 'text' : 'password'}
              className={styles.loginInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={isSubmitting}
            />
            <button
              type="button"
              className={styles.loginPasswordToggle}
              onClick={() => setShowPassword(v => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <IconEyeOff color="#7a7a9a" /> : <IconEye color="#7a7a9a" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className={styles.loginSubmitBtn}
          style={{
            background: canSubmit
              ? (portal === 'training' ? 'var(--color-primary)' : '#1a7a4a')
              : undefined,
          }}
          disabled={!canSubmit}
          aria-busy={isSubmitting ? 'true' : 'false'}
        >
          {isSubmitting ? 'Signing in…' : `Sign in to ${portal === 'training' ? 'Training' : 'Admin'} Portal`}
        </button>
      </form>

      <a href="#" className={styles.loginForgot}>Forgot password?</a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LandingPage
// ---------------------------------------------------------------------------

export function LandingPage(): JSX.Element {
  const [loginVisible, setLoginVisible] = useState(false);
  const [loginPortal, setLoginPortal] = useState<Portal>('training');

  function openLogin(portal: Portal = 'training') {
    setLoginPortal(portal);
    setLoginVisible(true);
  }

  function closeLogin() {
    setLoginVisible(false);
  }

  return (
    <div className={styles.shell}>

      {/* ------------------------------------------------------------------ */}
      {/* Left sidebar                                                         */}
      {/* ------------------------------------------------------------------ */}
      <aside className={styles.sidebar}>
        <a href="#" className={styles.sidebarLogo}>
          <div className={styles.sidebarLogoMark}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1.5" fill="white" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.5" />
            </svg>
          </div>
          <div className={styles.sidebarLogoText}>
            <span className={styles.sidebarLogoTitle}>ARIA</span>
            <span className={styles.sidebarLogoSub}>AI-Powered{'\n'}Insurance Learning{'\n'}Platform</span>
          </div>
        </a>

        <nav className={styles.sidebarNav}>
          <a href="#" className={`${styles.sidebarLink} ${styles.sidebarLinkActive}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Home
          </a>
          <a href="#" className={styles.sidebarLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            About
          </a>
          <a href="#" className={styles.sidebarLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            How It Works
          </a>
          <a href="#" className={styles.sidebarLink}>
            <IconBuilding color="currentColor" />
            For Organizations
          </a>
          <a href="#" className={styles.sidebarLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            Resources
          </a>
        </nav>

        <div className={styles.sidebarDivider} />

        <button className={styles.sidebarLoginBtn} onClick={() => openLogin('training')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          Login
        </button>
        <button className={styles.sidebarGetStartedBtn} onClick={() => openLogin('training')}>
          Get Started
        </button>
      </aside>

      {/* ------------------------------------------------------------------ */}
      {/* Main                                                                 */}
      {/* ------------------------------------------------------------------ */}
      <main className={styles.main}>

        {/* Top bar */}
        <header className={styles.topbar}>
          <nav className={styles.topbarNav}>
            <a href="#" className={`${styles.topbarLink} ${styles.topbarLinkActive}`}>Home</a>
            <a href="#" className={styles.topbarLink}>About</a>
            <a href="#" className={styles.topbarLink}>How It Works</a>
            <a href="#" className={styles.topbarLink}>For Organizations</a>
            <button className={styles.topbarLink} type="button">Resources <IconChevronDown /></button>
          </nav>
          <div className={styles.topbarActions}>
            <button className={styles.langBtn} type="button">
              <IconGlobe color="#4a4a6a" /> English <IconChevronDown color="#4a4a6a" />
            </button>
            <button className={styles.loginBtn} onClick={() => openLogin('training')} type="button">Login</button>
            <button className={styles.getStartedBtn} onClick={() => openLogin('training')} type="button">Get Started</button>
          </div>
        </header>

        {/* Hero */}
        <section className={styles.hero}>
          <div className={styles.heroLeft}>
            <div className={styles.heroBadge}>
              <IconStar color="#0055a5" />
              AI-Powered Learning
            </div>
            <h1 className={styles.heroHeadline}>
              AI-Powered Learning<br />
              for Insurance<br />
              <span className={styles.heroHeadlineAccent}>Field Agents</span>
            </h1>
            <p className={styles.heroSubline}>Upskill. Get Certified. Serve Better.</p>
            <p className={styles.heroDesc}>
              Personalized learning journeys, compliance training,
              and AI assistance — all in one platform.
            </p>
            <div className={styles.heroCtas}>
              <button className={styles.ctaPrimary} onClick={() => openLogin('training')} type="button">Get Started</button>
              <button className={styles.ctaSecondary} onClick={() => openLogin('training')} type="button">Request Demo</button>
            </div>
          </div>

          {/* Right side: toggles between mockup and login panel */}
          <div className={styles.heroRight}>
            {loginVisible ? (
              <LoginPanel initialPortal={loginPortal} onClose={closeLogin} />
            ) : (
              <>
                <div className={styles.mockupWrapper}>
                  <AppMockup />
                  <AgentIllustration />
                </div>
                <div className={styles.featureBadges}>
                  <div className={styles.featureBadge}>
                    <div className={styles.featureBadgeIcon} style={{ background: '#e8f0fb' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <div className={styles.featureBadgeText}>
                      <span className={styles.featureBadgeTitle}>AI Learning<br />Assistant</span>
                    </div>
                  </div>
                  <div className={styles.featureBadge}>
                    <div className={styles.featureBadgeIcon} style={{ background: '#e8f4ed' }}>
                      <IconUser color="#1a7a4a" />
                    </div>
                    <div className={styles.featureBadgeText}>
                      <span className={styles.featureBadgeTitle}>Role-based<br />Learning</span>
                    </div>
                  </div>
                  <div className={styles.featureBadge}>
                    <div className={styles.featureBadgeIcon} style={{ background: '#fef3e6' }}>
                      <IconShield color="#c95f00" />
                    </div>
                    <div className={styles.featureBadgeText}>
                      <span className={styles.featureBadgeTitle}>Track, Certify,<br />Stay Compliant</span>
                    </div>
                  </div>
                  <div className={styles.featureBadge}>
                    <div className={styles.featureBadgeIcon} style={{ background: '#f0e8fa' }}>
                      <IconBarChart color="#6b3fa0" />
                    </div>
                    <div className={styles.featureBadgeText}>
                      <span className={styles.featureBadgeTitle}>AI-Driven<br />Insights</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Stats bar */}
        <div className={styles.statsBar}>
          <div className={styles.statsInner}>
            <div className={styles.statItem}>
              <div className={styles.statIcon}><IconUsers color="#0055a5" /></div>
              <div><div className={styles.statValue}>10K+</div><div className={styles.statLabel}>Active Learners</div></div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statIcon}><IconBook color="#1a7a4a" /></div>
              <div><div className={styles.statValue}>500+</div><div className={styles.statLabel}>Courses</div></div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statIcon}>
                <svg width="36" height="36" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#dde1ed" strokeWidth="4" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#0055a5" strokeWidth="4"
                    strokeDasharray="96.47 101.5" strokeDashoffset="25.5" strokeLinecap="round" />
                </svg>
              </div>
              <div><div className={styles.statValue}>95%</div><div className={styles.statLabel}>Completion Rate</div></div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statIcon}><IconBuilding color="#6b3fa0" /></div>
              <div><div className={styles.statValue}>50+</div><div className={styles.statLabel}>Enterprises</div></div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statIcon}><IconAward color="#c95f00" /></div>
              <div><div className={styles.statValue}>100K+</div><div className={styles.statLabel}>Certificates Issued</div></div>
            </div>
          </div>
        </div>

        {/* Role selector */}
        <section className={styles.roleSection}>
          <h2 className={styles.roleSectionTitle}>Choose Your Access</h2>
          <div className={styles.roleCards}>

            <div className={styles.roleCard}>
              <div className={styles.roleCardIcon} style={{ background: '#e8f0fb' }}><IconUser color="#0055a5" /></div>
              <div className={styles.roleCardTitle}>I am an Agent</div>
              <p className={styles.roleCardDesc}>Access training, courses, certifications and more.</p>
              <button className={`${styles.roleCardBtn} ${styles.roleCardBtnBlue}`} onClick={() => openLogin('training')} type="button">Login as Agent</button>
            </div>

            <div className={styles.roleCard}>
              <div className={styles.roleCardIcon} style={{ background: '#e6f4ed' }}><IconSettings color="#1a7a4a" /></div>
              <div className={styles.roleCardTitle}>I am an Administrator</div>
              <p className={styles.roleCardDesc}>Create and manage courses, content and users.</p>
              <button className={`${styles.roleCardBtn} ${styles.roleCardBtnGreen}`} onClick={() => openLogin('admin')} type="button">Login as Admin</button>
            </div>

            <div className={styles.roleCard}>
              <div className={styles.roleCardIcon} style={{ background: '#f0e8fa' }}><IconBarChart color="#6b3fa0" /></div>
              <div className={styles.roleCardTitle}>I am a Manager / Governance</div>
              <p className={styles.roleCardDesc}>Assign training, view reports and insights.</p>
              <button className={`${styles.roleCardBtn} ${styles.roleCardBtnPurple}`} onClick={() => openLogin('training')} type="button">Login as Manager</button>
            </div>

            <div className={styles.roleCard}>
              <div className={styles.roleCardIcon} style={{ background: '#fef3e6' }}><IconUsers color="#c95f00" /></div>
              <div className={styles.roleCardTitle}>Explore as Guest</div>
              <p className={styles.roleCardDesc}>Demo our platform and experience AI Learn.</p>
              <button className={`${styles.roleCardBtn} ${styles.roleCardBtnOrange}`} onClick={() => openLogin('training')} type="button">Browse Now</button>
            </div>

          </div>
        </section>
      </main>
    </div>
  );
}
